/**
 * navDirectionSignService.js
 *
 * Renders editor-authored multi-arm direction signposts as REAL 3D meshes
 * inside the Matterport scene. Each direction sign is a vertical post
 * with up to ~4 directional arms; every arm has its own world-space yaw
 * and its own destination label.
 *
 * Visual styling is driven by `signVariant` (A–E), which were ported from
 * the Direction Signages design canvas. The variant resolves to a full
 * styling spec — post colour/radius/cap, arm background, text colour and
 * tip shape — applied uniformly across all arms on that post.
 *
 * Source of truth for the data:
 *   navHintsService.getAll().filter(h => h.type === 'directionSign')
 *
 * Hot-reload model: subscribes to navHintsService and rebuilds individual
 * meshes on add / update / remove. Re-paints arm canvas textures on
 * language change (setLanguage).
 *
 * Usage:
 *   navDirectionSignService.initialize(sdk, language)   — call once after SDK connects
 *   navDirectionSignService.setLanguage(lang)           — language change hook
 *   navDirectionSignService.destroy()
 */

import navHintsService from './navHintsService';

const COMPONENT_NAME = 'mp.navDirectionSign';

// Real-world reference dimensions (metres) at scale=1. Arm rig is
// deliberately compact — a real wayfinding signpost is much smaller
// than a billboard, and oversized arms read as cluttered in VR.
const REF_POST_HEIGHT = 2.4;     // total post height (ground → top cap)
const REF_ARM_WIDTH   = 0.72;    // horizontal length of one arm (slim board)
const REF_ARM_HEIGHT  = 0.16;    // vertical height of one arm (slim board)
const REF_ARM_SPACING = 0.22;    // vertical gap between arm centres
const REF_TOP_PAD     = 0.24;    // distance from post top to first arm centre

const ARM_TEX_W = 1152;
const ARM_TEX_H = 256;

const _meshRegistry = new Map();   // hintId → THREE.Group
// hintId → { armCanvases: HTMLCanvasElement[], armCanvasesBack: HTMLCanvasElement[] }
// Two canvases per arm: one for the front-facing plane (arrow on the
// outer / pole-far edge as drawn) and one for the back-facing plane
// (arrow mirrored to the opposite edge of the canvas, but text drawn
// normally so a viewer behind the post still reads it un-mirrored).
const _canvasRegistry = new Map();

// ─── Variant style table ─────────────────────────────────────────────
// Each variant resolves to a flat record consumed by the canvas painter
// and the SDK component. Each variant now declares a `material` for the
// post / base / arm — 'wood' | 'stone' | 'metal' — so the canvas painter
// can lay down a believable surface (grain, speckle, brushed lines)
// instead of flat colour. `grain` is the secondary streak colour used
// by the procedural texture.
const VARIANTS = {
  A: {
    label: 'A · Painted teak on honey oak',
    post:    { material: 'wood',  color: '#a87444', grain: '#6b4220', radius: 0.045, cap: { kind: 'flat',   color: '#6a4a2a' } },
    base:    { kind: 'plinth',    material: 'wood',  color: '#5a3a1c', grain: '#2e1a08', heightMul: 1.6, radiusMul: 4.0 },
    arm:     { material: 'wood',  fill: '#d97014', grain: '#9a4a0a',  text: '#2a1408', border: '#5a2a08', accent: '#2a1408' },
  },
  B: {
    label: 'B · Cream-painted plank with brass cap',
    post:    { material: 'wood',  color: '#5e3a1f', grain: '#36210e', radius: 0.05,  cap: { kind: 'kalash', color: '#d4a542' } },
    base:    { kind: 'stone',     material: 'stone', color: '#8a6238', grain: '#5a3e22', heightMul: 1.8, radiusMul: 4.2 },
    arm:     { material: 'wood',  fill: '#f4ecdb', grain: '#cdc0a0',  text: '#5a1f10', border: '#7a3018', accent: '#7a3018' },
  },
  C: {
    label: 'C · Charcoal-stained timber, brass band',
    post:    { material: 'wood',  color: '#22201c', grain: '#0e0c08', radius: 0.06,  cap: { kind: 'band',   color: '#f4a13a' } },
    base:    { kind: 'plinth',    material: 'stone', color: '#1a1612', grain: '#0e0c0a', heightMul: 2.0, radiusMul: 3.6 },
    arm:     { material: 'wood',  fill: '#22201c', grain: '#0e0c08',  text: '#fdf6e6', border: '#f4a13a', accent: '#f4a13a' },
  },
  D: {
    label: 'D · Whitewashed plank on stone',
    post:    { material: 'stone', color: '#8a7a6c', grain: '#5e503e', radius: 0.04,  cap: { kind: 'dome',   color: '#e07c1a' } },
    base:    { kind: 'ring',      material: 'metal', color: '#a07a52', grain: '#704a25', heightMul: 1.2, radiusMul: 4.5 },
    arm:     { material: 'wood',  fill: '#fbf7ee', grain: '#d8cfb8',  text: '#1a1410', border: '#7a6a52', accent: '#e07c1a' },
  },
  E: {
    label: 'E · Aged temple wood on sandstone',
    post:    { material: 'stone', color: '#a07a52', grain: '#6b4e30', radius: 0.07,  cap: { kind: 'kalash', color: '#e3c068' } },
    base:    { kind: 'stone',     material: 'stone', color: '#6b4e30', grain: '#3e2a14', heightMul: 2.2, radiusMul: 4.6 },
    arm:     { material: 'wood',  fill: '#5a3a18', grain: '#2e1c08',  text: '#f3d57a', border: '#2e1c08', accent: '#e3c068' },
  },
  // Variant F — heritage temple-themed signpost: powder-coated dark
  // bronze pole, matte ACP / painted-metal plaques with chevron
  // arrow tips, double-line border, mounted with brass clamp
  // brackets that carry visible bolts. Real-world targets:
  //   pole 2.3 m × 100 mm Ø · boards 680 mm × 200 mm × 18 mm
  //   chevron tip 90 mm · board pitch 330 mm.
  //
  // `material: 'acp'` is a fine-speckle matte finish (very subtle
  // micro-noise + scratches) used for the panel face and pole. The
  // unlit Matterport scene-component pipeline cannot use real
  // shading, so all highlights/shadows/AO are baked into the canvas.
  // Variant F uses a strict 3-colour palette:
  //   • DARK   #1a120e — post, post grain, text, border
  //   • BRASS  #c9933a — finial cap, mounting clamp, bolts, arrow accent
  //   • WHITE  #fdfcf6 — arm fill (with #ece4cd matte speckle from the
  //                       same warm-white family for the ACP grain).
  // Lit / dark facets and AO shading are derived from these three via
  // shadeColor(); no other base hex values are introduced.
  F: {
    label: 'F · Brass clamps · Off-white plaques',
    post:    {
      material: 'powder', color: '#1a120e', grain: '#1a120e',
      radius: 0.028, cap: { kind: 'finial', color: '#c9933a' },
      bandColor: '#c9933a', clamp: 'bracket',
    },
    base:    {
      // Small matching footing — low dark-powder ring so the post
      // doesn't visually float. Stays inside the 3-colour palette
      // (DARK / BRASS / WHITE) so it reads as part of the same sign.
      kind: 'ring', material: 'powder',
      color: '#1a120e', grain: '#1a120e',
      radiusMul: 2.4, heightMul: 1.1,
    },
    arm:     {
      material: 'acp', fill: '#fdfcf6', grain: '#ece4cd',
      text: '#1a120e', border: '#1a120e', accent: '#1a120e',
      // Flat rectangular plaque with a baked-in directional arrow on
      // the canvas. Avoids the seam between a separate chevron-tip
      // mesh and the textured body face that the previous design had.
      // The painted arrow + double border give clear direction at any
      // viewing distance.
      borderStyle: 'double',
      // Power-of-two canvas at high resolution — required for WebGL
      // to build a full mipmap chain (non-POT textures fall back to
      // mip-0 sampling, which makes the double-border lines look
      // jagged / pixelated at oblique angles). 4096×1024 keeps the
      // Devanagari and the inset border crisp at any viewing distance.
      texW: 4096, texH: 1024,
    },
    dims:    {
      postHeight: 2.3,
      armWidth: 0.70, armHeight: 0.20, armDepth: 0.018,
      armSpacing: 0.33, topPad: 0.30,
      // No chevron — real signage plaques are flat rectangles.
      tipLen: 0,
    },
  },
};

const getVariant = (key) => VARIANTS[key] || VARIANTS.A;

// ─── Procedural surface textures ─────────────────────────────────────
// Tiny seeded RNG so each call produces consistent grain (same hint
// always paints the same wood). Mulberry32 — fast, good enough for
// noise patterns.
function _rng(seed) {
  let s = seed >>> 0;
  return () => {
    s = (s + 0x6D2B79F5) >>> 0;
    let t = s;
    t = Math.imul(t ^ (t >>> 15), t | 1);
    t ^= t + Math.imul(t ^ (t >>> 7), t | 61);
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

/**
 * Paint a procedural material surface (wood / stone / metal) into the
 * current clipped path on `ctx`. The base colour lays down first, then
 * the grain detail is drawn on top with an alpha blend.
 *
 * Caller is responsible for any clipping (so the texture only fills
 * the arm/post silhouette).
 */
function paintMaterial(ctx, kind, x, y, w, h, baseColor, grainColor, seed = 1, opts = {}) {
  const { uniformBase = false } = opts;
  // Base fill — by default a slight vertical gradient gives the surface
  // form before grain detail. For textures that wrap around a cylinder
  // (post / collar / base), pass `uniformBase: true` so the wrap doesn't
  // produce a bright / dark seam down the cylinder circumference.
  if (uniformBase) {
    ctx.fillStyle = baseColor;
    ctx.fillRect(x, y, w, h);
  } else {
    const grad = ctx.createLinearGradient(0, y, 0, y + h);
    grad.addColorStop(0,    shadeColor(baseColor,  0.18));
    grad.addColorStop(0.5,  baseColor);
    grad.addColorStop(1,    shadeColor(baseColor, -0.18));
    ctx.fillStyle = grad;
    ctx.fillRect(x, y, w, h);
  }

  const rand = _rng(seed);

  if (kind === 'wood') {
    // Long horizontal grain streaks with occasional knots. Softer
    // alpha + thinner curves than before so the surface reads as
    // sanded / oiled timber rather than rough sawn.
    ctx.save();
    ctx.globalAlpha = 0.20;
    ctx.lineCap = 'round';
    const lines = Math.round(h * 0.30);
    for (let i = 0; i < lines; i++) {
      const yy = y + rand() * h;
      const len = w * (0.4 + rand() * 0.7);
      const xx = x + rand() * (w - len);
      const thickness = 0.4 + rand() * 1.1;
      ctx.lineWidth = thickness;
      ctx.strokeStyle = rand() > 0.5 ? grainColor : shadeColor(grainColor, 0.30);
      ctx.beginPath();
      // Slightly curved streak — quadratic with a small vertical offset.
      const ctrlY = yy + (rand() - 0.5) * 3;
      ctx.moveTo(xx, yy);
      ctx.quadraticCurveTo(xx + len / 2, ctrlY, xx + len, yy + (rand() - 0.5) * 1.5);
      ctx.stroke();
    }
    // Knots — small dark elliptical clusters, fewer + softer.
    const knots = Math.max(0, Math.floor(w / 480));
    ctx.globalAlpha = 0.40;
    for (let i = 0; i < knots; i++) {
      const kx = x + 80 + rand() * (w - 160);
      const ky = y + h * 0.3 + rand() * h * 0.4;
      const krx = 5 + rand() * 8;
      const kry = krx * (0.45 + rand() * 0.3);
      const knotGrad = ctx.createRadialGradient(kx, ky, 0, kx, ky, krx);
      knotGrad.addColorStop(0,   shadeColor(grainColor, -0.20));
      knotGrad.addColorStop(0.6, grainColor);
      knotGrad.addColorStop(1,   'rgba(0,0,0,0)');
      ctx.fillStyle = knotGrad;
      ctx.beginPath();
      ctx.ellipse(kx, ky, krx, kry, 0, 0, Math.PI * 2);
      ctx.fill();
    }
    ctx.restore();
  } else if (kind === 'stone') {
    // Speckled noise — fine dots in grain colour, with faint horizontal
    // bedding lines at random intervals.
    ctx.save();
    const speckles = Math.round((w * h) / 220);
    for (let i = 0; i < speckles; i++) {
      const sx = x + rand() * w;
      const sy = y + rand() * h;
      const r = 0.4 + rand() * 1.4;
      ctx.globalAlpha = 0.18 + rand() * 0.32;
      ctx.fillStyle = rand() > 0.55
        ? grainColor
        : shadeColor(baseColor, rand() > 0.5 ? 0.25 : -0.30);
      ctx.beginPath();
      ctx.arc(sx, sy, r, 0, Math.PI * 2);
      ctx.fill();
    }
    // Bedding lines — 2–3 faint horizontal cracks.
    ctx.globalAlpha = 0.18;
    ctx.strokeStyle = grainColor;
    const beds = 2 + Math.floor(rand() * 2);
    for (let i = 0; i < beds; i++) {
      const yy = y + h * (0.2 + rand() * 0.6);
      ctx.lineWidth = 0.6 + rand();
      ctx.beginPath();
      ctx.moveTo(x, yy);
      ctx.bezierCurveTo(
        x + w * 0.3, yy + (rand() - 0.5) * 2,
        x + w * 0.7, yy + (rand() - 0.5) * 2,
        x + w,       yy
      );
      ctx.stroke();
    }
    ctx.restore();
  } else if (kind === 'metal') {
    // Brushed-metal — many faint vertical (or horizontal) lines.
    ctx.save();
    ctx.globalAlpha = 0.22;
    ctx.lineWidth = 0.6;
    const lines = Math.round(w * 0.3);
    for (let i = 0; i < lines; i++) {
      const xx = x + rand() * w;
      ctx.strokeStyle = rand() > 0.5 ? grainColor : shadeColor(baseColor, 0.20);
      ctx.beginPath();
      ctx.moveTo(xx, y);
      ctx.lineTo(xx + (rand() - 0.5) * 6, y + h);
      ctx.stroke();
    }
    ctx.restore();
  } else if (kind === 'acp') {
    // Matte ACP / painted-metal panel — very fine speckle plus
    // occasional micro-scratches and a couple of faint dust streaks.
    // Reads as a non-glossy painted surface, not wood and not glossy
    // metal.
    ctx.save();
    // Fine speckle.
    const speckles = Math.round((w * h) / 90);
    for (let i = 0; i < speckles; i++) {
      const sx = x + rand() * w;
      const sy = y + rand() * h;
      const r = 0.3 + rand() * 0.9;
      ctx.globalAlpha = 0.10 + rand() * 0.18;
      ctx.fillStyle = rand() > 0.6
        ? grainColor
        : shadeColor(baseColor, rand() > 0.5 ? 0.10 : -0.14);
      ctx.beginPath();
      ctx.arc(sx, sy, r, 0, Math.PI * 2);
      ctx.fill();
    }
    // Micro-scratches \u2014 short hairlines, mostly horizontal.
    const scratches = Math.round(w / 60);
    ctx.lineCap = 'round';
    for (let i = 0; i < scratches; i++) {
      const sx = x + rand() * w;
      const sy = y + rand() * h;
      const len = 4 + rand() * 14;
      const ang = (rand() - 0.5) * 0.4;
      ctx.globalAlpha = 0.10 + rand() * 0.20;
      ctx.lineWidth = 0.4 + rand() * 0.5;
      ctx.strokeStyle = rand() > 0.5
        ? shadeColor(baseColor,  0.18)
        : shadeColor(baseColor, -0.18);
      ctx.beginPath();
      ctx.moveTo(sx, sy);
      ctx.lineTo(sx + Math.cos(ang) * len, sy + Math.sin(ang) * len);
      ctx.stroke();
    }
    ctx.restore();
  } else if (kind === 'powder') {
    // Powder-coated metal \u2014 dense fine speckle, no grain streaks. The
    // surface looks slightly rough (matte) without reading as wood or
    // brushed metal.
    ctx.save();
    const speckles = Math.round((w * h) / 60);
    for (let i = 0; i < speckles; i++) {
      const sx = x + rand() * w;
      const sy = y + rand() * h;
      const r = 0.25 + rand() * 0.7;
      ctx.globalAlpha = 0.14 + rand() * 0.22;
      ctx.fillStyle = rand() > 0.55
        ? shadeColor(baseColor,  0.10)
        : shadeColor(baseColor, -0.16);
      ctx.beginPath();
      ctx.arc(sx, sy, r, 0, Math.PI * 2);
      ctx.fill();
    }
    // A few subtle vertical brush marks so the post still reads as
    // a fabricated metal surface from up close.
    ctx.globalAlpha = 0.12;
    ctx.lineWidth = 0.5;
    const streaks = Math.round(w * 0.08);
    for (let i = 0; i < streaks; i++) {
      const xx = x + rand() * w;
      ctx.strokeStyle = grainColor;
      ctx.beginPath();
      ctx.moveTo(xx, y);
      ctx.lineTo(xx + (rand() - 0.5) * 4, y + h);
      ctx.stroke();
    }
    ctx.restore();
  }
}

// ─── Pole / base canvas-texture builders ─────────────────────────────
// Returns a `<canvas>` which can be wrapped in a THREE.CanvasTexture
// and applied to `MeshBasicMaterial({ map })`. The canvas is sized to
// produce roughly square texels on the cylindrical surface — wider
// (longer circumference) than tall but never below 256 to keep the
// grain readable up close.
function buildSurfaceCanvas(kind, baseColor, grainColor, w = 768, h = 1536, seed = 1, opts = {}) {
  const c = document.createElement('canvas');
  c.width = w; c.height = h;
  const ctx = c.getContext('2d');
  paintMaterial(ctx, kind, 0, 0, w, h, baseColor, grainColor, seed, opts);
  return c;
}

// (Per-instance textures are built directly inside the SDK component
// via `buildSurfaceCanvas` — a shared cache could be reintroduced here
// if texture creation cost becomes a profile concern.)

// ─── Arm text-region geometry ─────────────────────────────────────────
/**
 * Compute the canvas dimensions and horizontal text region for a given
 * variant. Mirrors the layout logic inside `paintArmCanvas` so we can
 * pre-measure label width without painting. textRegionW is identical
 * for the front/back canvases (only X position differs), so we return
 * a single value usable for measurement.
 */
function getArmTextLayout(variant) {
  const v = getVariant(variant);
  const W = v.arm.texW || ARM_TEX_W;
  const H = v.arm.texH || ARM_TEX_H;
  const isSinglePiece = v.arm.singlePiece === true && v.arm.tip === 'chevron';
  let bodyPx = W;
  if (isSinglePiece && v.dims) {
    const total = (v.dims.armWidth || 0) + (v.dims.tipLen || 0);
    bodyPx = total > 0 ? Math.round(W * (v.dims.armWidth / total)) : W;
  }
  const innerPadX = H * 0.30;
  const arrowSize = H * 0.50;
  const hideArrow = v.arm.hideArrow === true;
  let textRegionW;
  if (isSinglePiece) {
    textRegionW = bodyPx - innerPadX * 2;
  } else {
    const arrowReserve = hideArrow ? 0 : arrowSize + innerPadX * 0.4;
    textRegionW = W - innerPadX * 2 - arrowReserve;
  }
  return { W, H, textRegionW: Math.max(0, textRegionW) };
}

/**
 * Largest font-size (in px) that lets `label` fit within `textRegionW`
 * for the given variant geometry. Scales down from the variant default
 * (`H * 0.36 * fontScale`) until the measured glyph width plus a small
 * safety margin fits inside the outer border. A shared `<canvas>` is
 * reused across calls to avoid GC churn during repaints.
 */
let _measureCanvas = null;
function fitArmFontSize(ctxOrNull, label, family, H, textRegionW, fontScale) {
  const baseSize = Math.max(8, Math.round(H * 0.36 * fontScale));
  if (!label || textRegionW <= 0) return baseSize;
  // Use the supplied ctx when available (it already has letterSpacing
  // configured to match the final paint); fall back to a shared
  // measurement canvas when called outside `paintArmCanvas`.
  let ctx = ctxOrNull;
  if (!ctx) {
    if (!_measureCanvas) _measureCanvas = document.createElement('canvas');
    ctx = _measureCanvas.getContext('2d');
    if ('letterSpacing' in ctx) ctx.letterSpacing = '-0.5px';
  }
  // Reserve ~3% horizontal padding so glyphs don't visually kiss the
  // inner border line.
  const safeW = textRegionW * 0.97;
  ctx.font = family.replace('Xpx', `${baseSize}px`);
  const measured = ctx.measureText(label).width;
  if (measured <= safeW) return baseSize;
  // Before shrinking, try wrapping into two lines at the base size —
  // long destinations like "Shri Mangaleshwar Mahadev Mandir" stay
  // readable when split at a word boundary instead of shrunk to 40%.
  const words = label.split(/\s+/).filter(Boolean);
  if (words.length >= 2) {
    let bestMax = Infinity;
    for (let i = 1; i < words.length; i++) {
      const a = words.slice(0, i).join(' ');
      const b = words.slice(i).join(' ');
      const w = Math.max(ctx.measureText(a).width, ctx.measureText(b).width);
      if (w < bestMax) bestMax = w;
    }
    if (bestMax <= safeW) return baseSize;
    // Both lines still don't fit — shrink based on the widest line.
    const scaledWrap = Math.floor(baseSize * (safeW / bestMax));
    return Math.max(Math.round(baseSize * 0.55), scaledWrap);
  }
  const scaled = Math.floor(baseSize * (safeW / measured));
  // Never shrink below 40% of the base — beyond that the engraved
  // shadow/highlight passes stop reading as carved text.
  return Math.max(Math.round(baseSize * 0.4), scaled);
}

/**
 * Wrap a label into 1 or 2 lines so long destinations (e.g. "Shri
 * Mangaleshwar Mahadev Mandir") stay readable without shrinking the
 * font into illegibility. Returns `[line]` when the label fits on a
 * single line at `fontSize`, else picks the word-boundary split that
 * minimises the widest line. Falls back to a single line when the
 * label has only one word (Devanagari conjuncts and English
 * compounds aren't broken mid-word).
 */
function wrapArmLabel(ctxOrNull, label, family, fontSize, textRegionW, lineMode) {
  if (!label) return [''];
  let ctx = ctxOrNull;
  if (!ctx) {
    if (!_measureCanvas) _measureCanvas = document.createElement('canvas');
    ctx = _measureCanvas.getContext('2d');
    if ('letterSpacing' in ctx) ctx.letterSpacing = '-0.5px';
  }
  ctx.font = family.replace('Xpx', `${fontSize}px`);
  // Per-arm forced layout overrides:
  //   • '1' — never wrap, keep on one line (font is shrunk by the
  //     painter's auto-fit pipeline if the label is too long).
  //   • '2' — always split into two lines at the best word boundary,
  //     even if the label would technically fit on one line.
  if (lineMode === '1') return [label];
  const words = label.split(/\s+/).filter(Boolean);
  const force2 = lineMode === '2' && words.length >= 2;
  const safeW = textRegionW * 0.97;
  if (!force2 && ctx.measureText(label).width <= safeW) return [label];
  if (words.length < 2) return [label];
  let bestSplit = -1;
  let bestMax = Infinity;
  for (let i = 1; i < words.length; i++) {
    const a = words.slice(0, i).join(' ');
    const b = words.slice(i).join(' ');
    const w = Math.max(ctx.measureText(a).width, ctx.measureText(b).width);
    if (w < bestMax) { bestMax = w; bestSplit = i; }
  }
  if (bestSplit < 0) return [label];
  return [
    words.slice(0, bestSplit).join(' '),
    words.slice(bestSplit).join(' '),
  ];
}

// ─── Canvas painter (per arm) ─────────────────────────────────────────
/**
 * Paint a single arm's face onto a transparent canvas.
 *
 * `arrowOnLeft`:
 *   false — arrow icon on the canvas's right side (used by the
 *           front-facing plane: viewer sees arrow at the outer end).
 *   true  — arrow icon on the canvas's left side (used by the
 *           back-facing plane: when that plane is rotated 180° around
 *           the post's vertical axis, this puts the arrow at the same
 *           outer end in world space — so a viewer behind the post
 *           also sees the arrow pointing away from the pole).
 *
 * Text is always drawn left-to-right (never mirrored) regardless of
 * which side the arrow is on, so the label is readable from both sides.
 */
function paintArmCanvas(canvas, label, variant, arrowOnLeft, overrides = {}) {
  const v = getVariant(variant);
  // Per-variant texture dimensions — variant F uses a wider/taller
  // canvas because its arms are 700×200 mm rather than the default
  // 720×160 mm aspect.
  const W = v.arm.texW || ARM_TEX_W;
  const H = v.arm.texH || ARM_TEX_H;
  canvas.width = W;
  canvas.height = H;
  const ctx = canvas.getContext('2d');
  ctx.clearRect(0, 0, W, H);

  // Per-hint overrides take precedence over the variant defaults.
  // Empty strings (the unset sentinel from the editor) fall through.
  const fill   = overrides.fill   || v.arm.fill;
  const grain  = overrides.grain  || v.arm.grain;
  const text   = overrides.text   || v.arm.text;
  const accent = overrides.accent || v.arm.accent;
  const material = v.arm.material;
  const fontScale = Number.isFinite(overrides.fontScale) && overrides.fontScale > 0
    ? overrides.fontScale : 1.0;
  const hideArrow   = v.arm.hideArrow === true;
  const doubleBorder = v.arm.borderStyle === 'double';
  const borderColor = overrides.border || v.arm.border || shadeColor(fill, -0.55);
  // Single-piece silhouette (rectangle body + chevron tip painted as
  // one continuous shape with one outline). When `singlePiece` is on,
  // the canvas is mapped onto a `ShapeGeometry` decal that already
  // matches this silhouette, so we paint outside the silhouette with
  // FULL transparency rather than a darkened-rectangle mask.
  const isSinglePiece = v.arm.singlePiece === true && v.arm.tip === 'chevron';
  // Compute body / tip pixel split along canvas X. The geometry's
  // bodyW + tipLen maps 1:1 to the canvas width.
  let bodyPx = W;
  let tipPx  = 0;
  if (isSinglePiece && v.dims) {
    const total = (v.dims.armWidth || 0) + (v.dims.tipLen || 0);
    bodyPx = total > 0 ? Math.round(W * (v.dims.armWidth / total)) : W;
    tipPx  = W - bodyPx;
  }

  // Panel fills the *entire* canvas — there is no transparent padding,
  // because the front/back faces of the box are mapped 1:1 to this
  // texture and any transparent margin would let the side-face edge
  // colour show through, producing a visible border ring around the
  // arm. The corners are still rounded *visually* via inset shadows
  // and a soft vignette around the edges of the painted face.
  const x = 0, y = 0;
  const w = W;
  const h = H;
  const cornerR = isSinglePiece ? Math.min(H * 0.08, 18) : 0;

  // `buildPath` traces the silhouette of the visible plaque face.
  // For default (rectangular) variants it's a rounded rectangle
  // covering the full canvas; for single-piece chevron variants it
  // traces the rectangle body + pointed chevron tip, with the tip
  // on the right (front canvas) or the left (back canvas, after
  // the decal mesh is rotated 180° so this puts the tip on the
  // outer end in world space).
  const buildPath = () => {
    ctx.beginPath();
    if (isSinglePiece) {
      // Canvas-space silhouette of the combined body + chevron.
      // Slight corner radius `cornerR` on the body's inner edges
      // (next to the pole) softens the connection to the clamp.
      const tipApexY = h / 2;
      if (!arrowOnLeft) {
        // Tip on right (canvas X = W).
        ctx.moveTo(0 + cornerR, 0);
        ctx.lineTo(bodyPx, 0);
        ctx.lineTo(W, tipApexY);
        ctx.lineTo(bodyPx, h);
        ctx.lineTo(0 + cornerR, h);
        ctx.quadraticCurveTo(0, h, 0, h - cornerR);
        ctx.lineTo(0, 0 + cornerR);
        ctx.quadraticCurveTo(0, 0, 0 + cornerR, 0);
      } else {
        // Mirror: tip on left (canvas X = 0).
        ctx.moveTo(W - cornerR, 0);
        ctx.lineTo(tipPx, 0);
        ctx.lineTo(0, tipApexY);
        ctx.lineTo(tipPx, h);
        ctx.lineTo(W - cornerR, h);
        ctx.quadraticCurveTo(W, h, W, h - cornerR);
        ctx.lineTo(W, 0 + cornerR);
        ctx.quadraticCurveTo(W, 0, W - cornerR, 0);
      }
      ctx.closePath();
    } else {
      ctx.moveTo(x + cornerR, y);
      ctx.lineTo(x + w - cornerR, y);
      ctx.quadraticCurveTo(x + w, y, x + w, y + cornerR);
      ctx.lineTo(x + w, y + h - cornerR);
      ctx.quadraticCurveTo(x + w, y + h, x + w - cornerR, y + h);
      ctx.lineTo(x + cornerR, y + h);
      ctx.quadraticCurveTo(x, y + h, x, y + h - cornerR);
      ctx.lineTo(x, y + cornerR);
      ctx.quadraticCurveTo(x, y, x + cornerR, y);
      ctx.closePath();
    }
  };

  // ── Base fill ───────────────────────────────────────────────────
  // Single-piece variants paint *only* inside the silhouette so the
  // texture's transparent margin won't bleed onto the side bevel of
  // the geometry.
  if (isSinglePiece) {
    ctx.save();
    buildPath();
    ctx.clip();
    ctx.fillStyle = fill;
    ctx.fillRect(0, 0, W, H);
    ctx.restore();
  } else {
    ctx.fillStyle = fill;
    ctx.fillRect(0, 0, W, H);
  }

  // ── Material fill (wood grain / stone speckle / brushed metal) ───
  // Painted across the entire canvas so the box face is fully opaque,
  // or clipped to the silhouette for single-piece variants.
  ctx.save();
  if (isSinglePiece) {
    buildPath();
    ctx.clip();
  }
  // Use a stable seed per variant so repeated paints look identical.
  const seed = (variant ? variant.charCodeAt(0) : 65) * 7 + 11;
  paintMaterial(ctx, material || 'wood', 0, 0, W, H,
    fill, grain || shadeColor(fill, -0.3), seed);

  // Subtle vertical light wash on top of the grain — gives the panel
  // a sense of depth without flattening the texture. Skipped for the
  // matte ACP / painted-metal plaque (variant F) so the body face
  // stays a uniform cream that matches the chevron-tip mesh exactly.
  const wash = ctx.createLinearGradient(0, 0, 0, H);
  if (material === 'acp') {
    wash.addColorStop(0, 'rgba(255,255,255,0.00)');
    wash.addColorStop(1, 'rgba(255,255,255,0.00)');
  } else {
    wash.addColorStop(0,    'rgba(255,255,255,0.10)');
    wash.addColorStop(0.5,  'rgba(255,255,255,0.00)');
    wash.addColorStop(1,    'rgba(0,0,0,0.16)');
  }
  ctx.fillStyle = wash;
  ctx.fillRect(0, 0, W, H);
  ctx.restore();

  // ── Painted rounded-corner mask + vignette ──────────────────────
  // Single-piece variants don't need the dark outer mask (the
  // ShapeGeometry decal already discards anything outside the
  // silhouette). Default rectangular variants keep the legacy mask
  // so a BoxGeometry face still reads as a soft-cornered plaque.
  if (!isSinglePiece) {
    ctx.save();
    // 1) outer mask — paint everything outside the rounded path with
    //    a darkened wood tone so the visible front face appears rounded.
    ctx.beginPath();
    ctx.rect(0, 0, W, H);
    buildPath();
    ctx.fillStyle = withAlpha(shadeColor(fill, -0.55), 0.92);
    ctx.fill('evenodd');
    ctx.restore();
  }

  ctx.save();
  // 2) inside the rounded region — soft inner highlights/shadows.
  // Skipped for matte ACP plaques (variant F): the body face must
  // stay a uniform cream so it visually matches the chevron-tip mesh
  // (which has no painted edge AO). Any bottom/left darkening here
  // would re-introduce the seam between body and tip.
  if (material !== 'acp') {
    buildPath();
    ctx.clip();
    // top chamfer highlight — thin bright line + softer fall-off,
    // mimicking a 2–3 mm bevel catching warm ambient light.
    const topChamfer = ctx.createLinearGradient(0, 0, 0, h * 0.10);
    topChamfer.addColorStop(0, 'rgba(255,245,230,0.55)');
    topChamfer.addColorStop(1, 'rgba(255,245,230,0.00)');
    ctx.fillStyle = topChamfer;
    ctx.fillRect(0, 0, w, h * 0.10);
    // top highlight wash (broader, softer)
    const topHi = ctx.createLinearGradient(0, 0, 0, h * 0.22);
    topHi.addColorStop(0, 'rgba(255,255,255,0.16)');
    topHi.addColorStop(1, 'rgba(255,255,255,0.00)');
    ctx.fillStyle = topHi;
    ctx.fillRect(0, 0, w, h * 0.22);
    // bottom edge AO (deeper, contact-shadow feel) + matching chamfer
    // highlight just above it — fakes the underside of the bevel.
    const botSh = ctx.createLinearGradient(0, h * 0.78, 0, h);
    botSh.addColorStop(0, 'rgba(0,0,0,0.00)');
    botSh.addColorStop(0.7, 'rgba(0,0,0,0.18)');
    botSh.addColorStop(1, 'rgba(0,0,0,0.45)');
    ctx.fillStyle = botSh;
    ctx.fillRect(0, h * 0.78, w, h * 0.22);
    // Left-edge ambient occlusion — darker where the panel meets the
    // mounting clamp so the joint reads as recessed.
    const leftAO = ctx.createLinearGradient(0, 0, w * 0.06, 0);
    leftAO.addColorStop(0, 'rgba(0,0,0,0.32)');
    leftAO.addColorStop(1, 'rgba(0,0,0,0.00)');
    ctx.fillStyle = leftAO;
    ctx.fillRect(0, 0, w * 0.06, h);
  }
  ctx.restore();

  // ── Optional double-line border (variant F) ─────────────────────
  // Two concentric thin strokes inset from the canvas edge — gives
  // the painted plaque a subtle decorative outline without the heavy
  // routed-groove look used elsewhere. For single-piece chevron
  // variants we stroke the silhouette path itself (so the border
  // wraps around the chevron tip as a single continuous outline)
  // instead of falling back to an inset rectangle.
  if (doubleBorder) {
    ctx.save();
    ctx.strokeStyle = withAlpha(borderColor, 0.85);
    ctx.lineCap = 'round';
    ctx.lineJoin = 'round';
    if (isSinglePiece) {
      // Outer stroke — sits a few pixels inside the silhouette so
      // the line isn't clipped at the bevel rim of the geometry.
      ctx.lineWidth = Math.max(2, Math.round(H * 0.018));
      buildPath();
      // Use clip+stroke trick so the stroke is half-inside, then
      // stroking again gives a crisp inner edge.
      ctx.save();
      buildPath();
      ctx.clip();
      ctx.lineWidth = Math.max(3, Math.round(H * 0.030));
      buildPath();
      ctx.stroke();
      ctx.restore();
      // Inner stroke — a thinner concentric line, painted as a
      // path inset from the silhouette by ~6% of H.
      const inset = Math.round(H * 0.06);
      const tipApexY = H / 2;
      ctx.lineWidth = Math.max(1.2, Math.round(H * 0.010));
      ctx.beginPath();
      if (!arrowOnLeft) {
        ctx.moveTo(inset + cornerR, inset);
        ctx.lineTo(bodyPx, inset);
        // Chevron inset: shift the apex inward by `inset` along
        // each chevron edge — approximated by reducing tip reach.
        const tipInset = inset * 1.3;
        ctx.lineTo(W - tipInset, tipApexY);
        ctx.lineTo(bodyPx, H - inset);
        ctx.lineTo(inset + cornerR, H - inset);
        ctx.quadraticCurveTo(inset, H - inset, inset, H - inset - cornerR);
        ctx.lineTo(inset, inset + cornerR);
        ctx.quadraticCurveTo(inset, inset, inset + cornerR, inset);
      } else {
        ctx.moveTo(W - inset - cornerR, inset);
        ctx.lineTo(tipPx, inset);
        const tipInset = inset * 1.3;
        ctx.lineTo(tipInset, tipApexY);
        ctx.lineTo(tipPx, H - inset);
        ctx.lineTo(W - inset - cornerR, H - inset);
        ctx.quadraticCurveTo(W - inset, H - inset, W - inset, H - inset - cornerR);
        ctx.lineTo(W - inset, inset + cornerR);
        ctx.quadraticCurveTo(W - inset, inset, W - inset - cornerR, inset);
      }
      ctx.closePath();
      ctx.stroke();
    } else {
      // Filled-band frames (NOT strokes). Thin canvas strokes get
      // dashed-out by GPU mipmap downsampling at viewing distance —
      // even thick ones, because each mip halves the texel count and
      // any line < 2 texels at a given mip level vanishes for the
      // pixels that sample that mip. Solid filled rectangles are
      // immune to this: every pixel inside the band is the same
      // colour, so any mip level samples the band correctly.
      //
      // Layout:  ┌─ outer band (border colour) ─┐
      //          │   ┌─ gap (plaque fill) ─┐    │
      //          │   │  ┌ inner band ┐     │    │
      //          │   │  │   text     │     │    │
      //          │   │  └────────────┘     │    │
      //          │   └─────────────────────┘    │
      //          └──────────────────────────────┘
      const snap = (n) => Math.round(n);
      const outerStart = snap(H * 0.085); // outer band starts here
      const outerEnd   = snap(H * 0.135); // (band thickness ~5% H)
      const innerStart = snap(H * 0.205); // inner band starts here
      const innerEnd   = snap(H * 0.235); // (band thickness ~3% H)
      ctx.fillStyle = borderColor;
      // Helper: draw a filled-frame band as 4 rectangles.
      const drawBand = (i0, i1) => {
        const t = i1 - i0;
        // Top strip
        ctx.fillRect(i0, i0, W - i0 * 2, t);
        // Bottom strip
        ctx.fillRect(i0, H - i1, W - i0 * 2, t);
        // Left strip
        ctx.fillRect(i0, i1, t, H - i1 * 2);
        // Right strip
        ctx.fillRect(W - i1, i1, t, H - i1 * 2);
      };
      drawBand(outerStart, outerEnd);
      drawBand(innerStart, innerEnd);
    }
    ctx.restore();
  }

  // ── Engraved arrow glyph + text ──────────────────────────────────
  // To sell "carved into wood", we draw each glyph three times:
  //   1. a darker shadow offset down-right (the cut depth shadow),
  //   2. a lighter highlight offset up-left (the chamfered upper rim),
  //   3. the main fill colour on top.
  // The result reads as a chiselled / routed groove rather than a
  // sticker laid on the panel.
  // Variants with a thin matte plaque (e.g. ACP) should engrave a
  // little deeper so the lettering reads from a distance \u2014 the
  // multi-pass shadow/highlight stack fakes a 1\u20132 mm groove.
  const engraveDepthMul = (material === 'acp') ? 1.6 : 1.0;
  const engraveDark   = withAlpha(shadeColor(fill, -0.55), 0.85);
  const engraveLight  = withAlpha(shadeColor(fill,  0.45), 0.55);
  const engraveOffset = Math.max(2, Math.round(h * 0.012 * engraveDepthMul));

  // Arrow position — outer end of the panel, away from the pole.
  // `innerPadX` keeps the arrow tip clear of the inner border band
  // (which sits between 0.205H and 0.235H from each edge). Bumping
  // pad to 0.30H leaves a comfortable gap between the arrow tip and
  // the inner frame line so they don't visually touch.
  const innerPadX = h * 0.30;
  const arrowSize = h * 0.50;
  const arrowCx = arrowOnLeft ? (innerPadX + arrowSize / 2)
                              : (W - innerPadX - arrowSize / 2);
  const arrowCy = H / 2;
  const ah = arrowSize;
  const arrowPath = () => {
    ctx.beginPath();
    ctx.moveTo(-ah * 0.40, -ah * 0.18);
    ctx.lineTo( ah * 0.10, -ah * 0.18);
    ctx.lineTo( ah * 0.10, -ah * 0.40);
    ctx.lineTo( ah * 0.50,  0);
    ctx.lineTo( ah * 0.10,  ah * 0.40);
    ctx.lineTo( ah * 0.10,  ah * 0.18);
    ctx.lineTo(-ah * 0.40,  ah * 0.18);
    ctx.closePath();
  };

  // Engraved arrow — skipped when the variant uses a physical
  // chevron-tipped arm so the directional cue isn't doubled up.
  if (!hideArrow) {
    ctx.save();
    ctx.translate(arrowCx, arrowCy);
    if (arrowOnLeft) ctx.scale(-1, 1); // mirror so arrow points outward
    // 1) bottom-right shadow (the depth of the cut)
    ctx.translate(engraveOffset, engraveOffset);
    ctx.fillStyle = engraveDark;
    arrowPath();
    ctx.fill();
    // 2) top-left highlight (the chamfered upper rim of the cut)
    ctx.translate(-engraveOffset * 2, -engraveOffset * 2);
    ctx.fillStyle = engraveLight;
    arrowPath();
    ctx.fill();
    // 3) main fill — accent colour, sitting at the bottom of the groove
    ctx.translate(engraveOffset, engraveOffset);
    ctx.fillStyle = accent || text;
    arrowPath();
    ctx.fill();
    ctx.restore();
  }

  // Engraved text — anchored on the side opposite to the arrow,
  // or centred when the arrow glyph is hidden.
  if (label) {
    ctx.save();
    ctx.textBaseline = 'middle';
    ctx.textAlign = 'center';

    let textRegionX, textRegionW;
    if (isSinglePiece) {
      // Keep the label centred within the rectangular body only —
      // the chevron tip is reserved for silhouette + border.
      if (!arrowOnLeft) {
        textRegionX = innerPadX;
        textRegionW = bodyPx - innerPadX * 2;
      } else {
        textRegionX = tipPx + innerPadX;
        textRegionW = (W - tipPx) - innerPadX * 2;
      }
    } else {
      // Centre the label in the visible label area — the region
      // between the post-side edge of the plaque and the inner
      // edge of the arrow glyph. Centring across the full canvas
      // makes the text read off-centre because the arrow eats
      // ~0.55H on one side while the inner edge has a much smaller
      // clamp wash, biasing the apparent midpoint toward the arrow.
      // Reserving the arrow side restores symmetric whitespace
      // around the destination text.
      const armPad = innerPadX;          // matches arrow's outer pad
      const arrowReserve = hideArrow ? 0 : arrowSize + innerPadX * 0.4;
      if (arrowOnLeft) {
        textRegionX = armPad + arrowReserve;
        textRegionW = W - textRegionX - armPad;
      } else {
        textRegionX = armPad;
        textRegionW = (W - armPad - arrowReserve) - armPad;
      }
    }

    // Devanagari rendering: prefer Noto Sans Devanagari at weight 900
    // (clean, high-contrast for matte plaques). Fallback chain keeps
    // English labels readable in a serif when no Devanagari glyphs
    // are present.
    const family = '900 Xpx "Noto Sans Devanagari", "Noto Serif Devanagari", "Noto Serif", "Inter", Georgia, serif';

    // Auto-fit font: start from the variant default and shrink so the
    // label fits inside the available textRegionW (i.e. inside the
    // outer border line, never overlapping it). When `fontSizePx` is
    // supplied via overrides (the per-hint min computed across all
    // arms — see `_ensureCanvases`), use it directly so every arm in
    // a single signage shares the same font size for a uniform look.
    let fontSize;
    if (Number.isFinite(overrides.fontSizePx) && overrides.fontSizePx > 0) {
      fontSize = overrides.fontSizePx;
    } else {
      fontSize = fitArmFontSize(ctx, label, family, H, textRegionW, fontScale);
    }
    ctx.font = family.replace('Xpx', `${fontSize}px`);
    // Tighter kerning makes Devanagari conjuncts read as a single
    // word rather than spaced syllables. Supported in Chromium 99+
    // and Safari 16+; ignored gracefully elsewhere.
    if ('letterSpacing' in ctx) ctx.letterSpacing = '-0.5px';

    const tx = textRegionX + textRegionW / 2;
    // Wrap the label into 1–2 lines at the chosen font size so long
    // destinations break cleanly at a word boundary instead of being
    // crushed onto a single line. Single-word labels (or labels that
    // already fit) stay on one line. The per-arm `lineMode` override
    // ('1' = force single, '2' = force split) takes precedence over
    // auto-fit decisions.
    const lines = wrapArmLabel(ctx, label, family, fontSize, textRegionW, overrides.lineMode);
    const lineHeight = Math.round(fontSize * 1.05);
    const totalH = lineHeight * lines.length;
    const startY = (H / 2) - (totalH / 2) + (lineHeight / 2);

    for (let li = 0; li < lines.length; li++) {
      const line = lines[li];
      const ty = startY + li * lineHeight;
      // 1) shadow / cut depth
      ctx.fillStyle = engraveDark;
      ctx.fillText(line, tx + engraveOffset, ty + engraveOffset);
      // 2) highlight / chamfer
      ctx.fillStyle = engraveLight;
      ctx.fillText(line, tx - engraveOffset, ty - engraveOffset);
      // 3) main fill
      ctx.fillStyle = text;
      ctx.fillText(line, tx, ty);
    }
    ctx.restore();
  }
}

// ─── Colour helpers ─────────────────────────────────────────────────
// Tiny hex-only colour utilities so the canvas painter can derive
// gradient stops without pulling a dependency. Both functions accept
// '#rgb', '#rrggbb' or '#rrggbbaa' and return '#rrggbb'/'rgba()'.
function _parseHex(hex) {
  let s = String(hex || '#000').replace('#', '');
  if (s.length === 3) s = s.split('').map((c) => c + c).join('');
  if (s.length < 6) s = (s + '000000').slice(0, 6);
  return [
    parseInt(s.slice(0, 2), 16),
    parseInt(s.slice(2, 4), 16),
    parseInt(s.slice(4, 6), 16),
  ];
}
function shadeColor(hex, amt) {
  // amt: -1..1; positive lightens, negative darkens.
  const [r, g, b] = _parseHex(hex);
  const f = (c) => Math.round(amt >= 0 ? c + (255 - c) * amt : c * (1 + amt));
  const to2 = (n) => Math.max(0, Math.min(255, n)).toString(16).padStart(2, '0');
  return `#${to2(f(r))}${to2(f(g))}${to2(f(b))}`;
}
function withAlpha(hex, a) {
  const [r, g, b] = _parseHex(hex);
  return `rgba(${r},${g},${b},${a})`;
}

// ─── SDK Scene component ─────────────────────────────────────────────
function NavDirectionSignComponent() {
  this.inputs = {
    hintId: '',
    worldX: 0, worldY: 0, worldZ: 0,
    rotationYDeg: 0,   // base post rotation (informational; arms have own yaws)
    scale: 1.0,
    variantKey: 'A',
    armSpec: [],       // [{ yaw, dir }]
    // Per-hint colour overrides — empty string = use variant default.
    armFill: '',
    armGrain: '',
    armBorder: '',
    postColor: '',
    capColor: '',
    bandColor: '',
  };
  this._group = null;
  this._materials = [];
  this._textures = [];
}

NavDirectionSignComponent.prototype.onInit = function () {
  try {
    const THREE = this.context.three;
    const {
      hintId, worldX, worldY, worldZ,
      rotationYDeg, scale, variantKey, armSpec,
      armFill, armGrain, postColor, capColor, bandColor,
      armBorder,
    } = this.inputs;

    const vRaw = getVariant(variantKey);
    // Apply per-hint colour overrides on top of the variant palette.
    // A blank override string means "use variant default".
    const v = {
      ...vRaw,
      arm: {
        ...vRaw.arm,
        fill:   armFill   || vRaw.arm.fill,
        grain:  armGrain  || vRaw.arm.grain,
        border: armBorder || vRaw.arm.border,
      },
      post: {
        ...vRaw.post,
        color:     postColor || vRaw.post.color,
        bandColor: bandColor || vRaw.post.bandColor,
        cap: {
          ...vRaw.post.cap,
          color: capColor || vRaw.post.cap.color,
        },
      },
    };
    const reg = _canvasRegistry.get(hintId);
    if (!reg) {
      console.warn(`🪧 NavDirectionSign: no canvas registered for "${hintId}"`);
      return;
    }

    const group = new THREE.Group();

    // Helper: MeshBasicMaterial doesn't depend on scene lighting so the
    // sign always renders with predictable colour inside the Matterport
    // viewer (which doesn't expose its directional/ambient lights to
    // user-registered scene components). Per-part shading is faked by
    // sampling slightly-tinted variants of each base colour for top
    // vs side surfaces.
    //
    // FrontSide (Three.js default) is used for every material below.
    // The post / cap / band / clamp / arm meshes are all closed solids
    // so backfaces are never legitimately visible; rendering them
    // (DoubleSide) was the source of thin yellow streaks that flickered
    // across the screen at certain camera angles when the brass cap or
    // band's far face clipped the camera near plane.
    const basicMat = (color) => new THREE.MeshBasicMaterial({
      color: new THREE.Color(color),
      side: THREE.FrontSide,
    });

    // Build a fresh texture on demand so each instance owns its own
    // canvas (cheap — a few hundred KB) and gets disposed cleanly with
    // the rest of the sign on `onDestroy`.
    const renderer = this.context.renderer;
    const maxAniso = renderer?.capabilities?.getMaxAnisotropy
      ? renderer.capabilities.getMaxAnisotropy()
      : 1;
    const makeSurfaceMat = (kind, baseColor, grainColor, repeatX = 2, repeatY = 1, rotate = false, opts = {}) => {
      // Use power-of-two canvas sizes so WebGL can build a full mipmap
      // chain — without this, the surface pixelates / shimmers when
      // viewed from a distance because the GPU samples the mip-0 level
      // at sub-pixel scale. 1024×2048 keeps the post side / arm edge
      // textures crisp at the close-up viewing distance typical inside
      // a Matterport sweep (the previous 512×1024 was visibly blocky
      // when standing right next to the signpost).
      const canvas = buildSurfaceCanvas(kind, baseColor, grainColor, 1024, 2048,
        (baseColor.charCodeAt(1) || 65) * 7 + 13, opts);
      const tex = new THREE.CanvasTexture(canvas);
      tex.wrapS = THREE.RepeatWrapping;
      tex.wrapT = THREE.RepeatWrapping;
      tex.repeat.set(repeatX, repeatY);
      if (rotate) {
        // Rotate the texture 90° so wood grain runs along the post's
        // length (parallel to V) instead of around its circumference.
        tex.center.set(0.5, 0.5);
        tex.rotation = Math.PI / 2;
      }
      tex.minFilter = THREE.LinearMipmapLinearFilter;
      tex.magFilter = THREE.LinearFilter;
      tex.generateMipmaps = true;
      tex.anisotropy = maxAniso;
      tex.needsUpdate = true;
      this._textures.push(tex);
      return new THREE.MeshBasicMaterial({ map: tex, side: THREE.FrontSide });
    };

    // ── Ground base ─────────────────────────────────────────────────
    // A small footing where the post meets the ground — anchors the
    // sign visually and prevents the "post floating in mid-air" look.
    const baseSpec = v.base || { kind: 'none' };
    const postR = v.post.radius * scale;
    let baseTopY = 0;
    if (baseSpec.kind && baseSpec.kind !== 'none') {
      const baseH = postR * (baseSpec.heightMul ?? 1.6);
      const baseR = postR * (baseSpec.radiusMul ?? 4.0);
      const baseColorMain = baseSpec.color || '#2a2520';
      const baseGrain     = baseSpec.grain || shadeColor(baseColorMain, -0.3);
      const baseKind      = baseSpec.material || 'stone';
      const baseMat       = makeSurfaceMat(baseKind, baseColorMain, baseGrain, 2, 1, false, { uniformBase: true });
      const baseTopMat    = basicMat(shadeColor(baseColorMain,  0.18));
      const baseBottomMat = basicMat(shadeColor(baseColorMain, -0.18));
      let baseGeo;
      if (baseSpec.kind === 'plinth') {
        // Tapered square plinth — slightly wider at the bottom.
        baseGeo = new THREE.CylinderGeometry(baseR * 0.85, baseR, baseH, 4);
      } else if (baseSpec.kind === 'stone') {
        // Octagonal stone block (temple-style).
        baseGeo = new THREE.CylinderGeometry(baseR * 0.95, baseR * 1.05, baseH, 8);
      } else {
        // 'ring' — low metal collar at the ground.
        baseGeo = new THREE.CylinderGeometry(baseR, baseR, baseH, 24);
      }
      // Cylinder face material order: [side, top, bottom].
      const base = new THREE.Mesh(baseGeo, [baseMat, baseTopMat, baseBottomMat]);
      base.position.set(0, baseH / 2, 0);
      group.add(base);
      // Subtle bevel cap on top of the plinth/stone for realism.
      if (baseSpec.kind !== 'ring') {
        const bevelGeo = new THREE.CylinderGeometry(
          baseR * 0.78, baseR * 0.92, baseH * 0.18,
          baseSpec.kind === 'stone' ? 8 : 4,
        );
        const bevelMat = basicMat(shadeColor(baseColorMain, 0.10));
        const bevel = new THREE.Mesh(bevelGeo, bevelMat);
        bevel.position.set(0, baseH + baseH * 0.09, 0);
        group.add(bevel);
        baseTopY = baseH + baseH * 0.18;
      } else {
        baseTopY = baseH;
      }
    }

    // ── Ground contact shadow ───────────────────────────────────────
    // The Matterport scene component pipeline cannot use real lights
    // so we fake a soft contact shadow under the post with a thin
    // ground-aligned disc carrying a radial-gradient alpha texture.
    // Sits a few millimetres above the floor to avoid z-fighting.
    if (v.dims?.groundShadow !== false) {
      const shadowSize = postR * 14;
      const shadowCanvas = document.createElement('canvas');
      shadowCanvas.width = 256; shadowCanvas.height = 256;
      const sctx = shadowCanvas.getContext('2d');
      const sgrad = sctx.createRadialGradient(128, 128, 8, 128, 128, 120);
      sgrad.addColorStop(0,    'rgba(0,0,0,0.55)');
      sgrad.addColorStop(0.45, 'rgba(0,0,0,0.22)');
      sgrad.addColorStop(1,    'rgba(0,0,0,0.00)');
      sctx.fillStyle = sgrad;
      sctx.fillRect(0, 0, 256, 256);
      const shadowTex = new THREE.CanvasTexture(shadowCanvas);
      shadowTex.minFilter = THREE.LinearMipmapLinearFilter;
      shadowTex.magFilter = THREE.LinearFilter;
      shadowTex.generateMipmaps = true;
      shadowTex.anisotropy = maxAniso;
      this._textures.push(shadowTex);
      const shadowMat = new THREE.MeshBasicMaterial({
        map: shadowTex, transparent: true, depthWrite: false,
      });
      const shadowGeo = new THREE.PlaneGeometry(shadowSize, shadowSize);
      const shadowMesh = new THREE.Mesh(shadowGeo, shadowMat);
      shadowMesh.rotation.x = -Math.PI / 2;
      shadowMesh.position.set(0, 0.008, 0); // 8 mm above floor
      shadowMesh.renderOrder = -1;
      group.add(shadowMesh);
      this._materials.push(shadowMat);
    }

    // ── Post ────────────────────────────────────────────────────────
    const dims = v.dims || {};
    const postH = (dims.postHeight ?? REF_POST_HEIGHT) * scale;
    // Slight taper (top thinner than bottom) for realism.
    const postGeo = new THREE.CylinderGeometry(postR * 0.92, postR, postH, 24);
    const postSideMat   = makeSurfaceMat(
      v.post.material || 'wood',
      v.post.color,
      v.post.grain || shadeColor(v.post.color, -0.3),
      v.post.material === 'wood' ? 2 : 2,
      v.post.material === 'wood' ? 1 : 2,
      v.post.material === 'wood',
      { uniformBase: true },
    );
    const postTopMat    = basicMat(shadeColor(v.post.color,  0.20));
    const postBottomMat = basicMat(shadeColor(v.post.color, -0.20));
    const post = new THREE.Mesh(postGeo, [postSideMat, postTopMat, postBottomMat]);
    post.position.set(0, baseTopY + postH / 2, 0);
    group.add(post);
    // Effective top of post (used by cap + arm placement).
    const postTopY = baseTopY + postH;

    // ── Cap ────────────────────────────────────────────────────────
    const capMat       = basicMat(v.post.cap.color);
    const capLitMat    = basicMat(shadeColor(v.post.cap.color,  0.18));
    const capDarkMat   = basicMat(shadeColor(v.post.cap.color, -0.18));
    if (v.post.cap.kind === 'kalash') {
      // Lower bell + upper bulb (simplified kalash silhouette).
      const bellGeo = new THREE.ConeGeometry(postR * 3.0, postR * 4.0, 16);
      const bell = new THREE.Mesh(bellGeo, capMat);
      bell.position.set(0, postTopY + postR * 1.6, 0);
      group.add(bell);
      const bulbGeo = new THREE.SphereGeometry(postR * 1.4, 16, 12);
      const bulb = new THREE.Mesh(bulbGeo, capLitMat);
      bulb.position.set(0, postTopY + postR * 4.4, 0);
      group.add(bulb);
    } else if (v.post.cap.kind === 'dome') {
      const domeGeo = new THREE.SphereGeometry(postR * 2.2, 18, 10, 0, Math.PI * 2, 0, Math.PI / 2);
      const dome = new THREE.Mesh(domeGeo, capMat);
      dome.position.set(0, postTopY, 0);
      group.add(dome);
    } else if (v.post.cap.kind === 'band') {
      const bandGeo = new THREE.CylinderGeometry(postR * 1.25, postR * 1.25, postR * 2.0, 24);
      const band = new THREE.Mesh(bandGeo, [capMat, capLitMat, capDarkMat]);
      band.position.set(0, postTopY - postR * 1.0, 0);
      group.add(band);
    } else if (v.post.cap.kind === 'finial') {
      // Decorative brass finial: short collar + smooth ball + tiny
      // pointed cone on top. Reads as a polished metallic ornament.
      const collarGeo = new THREE.CylinderGeometry(postR * 0.78, postR * 0.95, postR * 0.55, 18);
      const collar = new THREE.Mesh(collarGeo, [capMat, capLitMat, capDarkMat]);
      collar.position.set(0, postTopY + postR * 0.275, 0);
      group.add(collar);
      const ballGeo = new THREE.SphereGeometry(postR * 1.15, 22, 16);
      const ball = new THREE.Mesh(ballGeo, capLitMat);
      ball.position.set(0, postTopY + postR * 0.55 + postR * 1.05, 0);
      group.add(ball);
      const tipGeo = new THREE.ConeGeometry(postR * 0.32, postR * 0.7, 14);
      const tip = new THREE.Mesh(tipGeo, capMat);
      tip.position.set(0, postTopY + postR * 0.55 + postR * 2.1 + postR * 0.35, 0);
      group.add(tip);
    } else if (v.post.cap.kind === 'knob') {
      // Small finial: a short stem + a ball, both painted in the cap
      // colour (the reference design has a brown knob matching the
      // post, with the brass detailing left to the arm-joint bands).
      const stemH = postR * 1.2;
      const stemGeo = new THREE.CylinderGeometry(postR * 0.55, postR * 0.55, stemH, 16);
      const stem = new THREE.Mesh(stemGeo, [capMat, capLitMat, capDarkMat]);
      stem.position.set(0, postTopY + stemH / 2, 0);
      group.add(stem);
      const ballGeo = new THREE.SphereGeometry(postR * 1.1, 18, 14);
      const ball = new THREE.Mesh(ballGeo, capLitMat);
      ball.position.set(0, postTopY + stemH + postR * 0.9, 0);
      group.add(ball);
    } else {
      // 'flat' — squared cap
      const flatGeo = new THREE.BoxGeometry(postR * 3.0, postR * 0.8, postR * 3.0);
      const flat = new THREE.Mesh(flatGeo, capMat);
      flat.position.set(0, postTopY + postR * 0.4, 0);
      group.add(flat);
    }

    // ── Arms ────────────────────────────────────────────────────────
    const armW = (dims.armWidth  ?? REF_ARM_WIDTH ) * scale;
    // When a hint has at least one wrapped (2-line) label, the canvas
    // painter stores `armHeightMul` on the registry so all arms in
    // this signage are stretched vertically in 3D — the texture
    // stretches with them, giving 2-line text room to breathe inside
    // the engraved double border without letters touching the rim.
    const armHeightMul = (reg.armHeightMul && reg.armHeightMul > 0) ? reg.armHeightMul : 1.0;
    const armH = (dims.armHeight ?? REF_ARM_HEIGHT) * scale * armHeightMul;
    // Real-world arm thickness — a wooden plaque is thinner than it is
    // tall but still has noticeable depth. Sized to feel like a board
    // rather than a beam.
    const armDepth = (dims.armDepth != null)
      ? dims.armDepth * scale
      : Math.max(postR * 0.40, armH * 0.16);
    const spacing  = (dims.armSpacing ?? REF_ARM_SPACING) * scale;
    const topPad   = (dims.topPad     ?? REF_TOP_PAD    ) * scale;
    const tipLen   = (dims.tipLen     ?? 0              ) * scale;

    // For chevron-tipped arms, the BoxGeometry covers only the
    // rectangular body; the pointed tip is a separate triangular
    // prism appended at the outer end. When the variant declares
    // `singlePiece: true`, the body + chevron are instead built as
    // ONE combined silhouette (core ExtrudeGeometry + two ShapeGeometry
    // decals carrying the canvas texture), so the double-border
    // wraps as a single continuous outline.
    const useChevron    = v.arm.tip === 'chevron' && tipLen > 0;
    const isSinglePiece = useChevron && v.arm.singlePiece === true;
    const useShapeArm   = isSinglePiece;
    const bodyW = useChevron ? Math.max(armW - tipLen, armW * 0.4) : armW;
    const armGeo = useShapeArm ? null : new THREE.BoxGeometry(bodyW, armH, armDepth);

    armSpec.forEach((a, i) => {
      // Two textures — one per facing side. The back canvas is painted
      // with the arrow on the opposite edge so that the arrow always
      // points away from the pole regardless of which side the viewer
      // is on. Side (edge) faces use a wood-grain material to match
      // the panel.
      const texFront = new THREE.CanvasTexture(reg.armCanvases[i]);
      const texBack  = new THREE.CanvasTexture(reg.armCanvasesBack[i]);
      // Build full mipmap chain + anisotropic filtering so the arm
      // text stays crisp when viewed from a distance instead of
      // pixelating / shimmering at sub-pixel scale.
      [texFront, texBack].forEach((t) => {
        t.minFilter = THREE.LinearMipmapLinearFilter;
        t.magFilter = THREE.LinearFilter;
        t.generateMipmaps = true;
        t.anisotropy = maxAniso;
        t.needsUpdate = true;
      });

      // Edge faces use the same wood-grain texture as the panel —
      // slightly darkened — so the arm's thickness reads as the side
      // of a single wooden plaque rather than a contrasting strip.
      // Skipped for single-piece variants whose silhouette is built
      // from a core ExtrudeGeometry that owns its own material.
      const armGrain = v.arm.grain || shadeColor(v.arm.fill, -0.3);
      const matFace = (map) => new THREE.MeshBasicMaterial({ map });
      let matEdge = null, matEdgeTop = null, matEdgeBot = null;
      let matFront = null, matBack = null, armMats = null;
      if (!useShapeArm) {
        matEdge    = makeSurfaceMat(v.arm.material || 'wood',
          shadeColor(v.arm.fill, -0.18), armGrain, 1, 1);
        matEdgeTop = makeSurfaceMat(v.arm.material || 'wood',
          shadeColor(v.arm.fill,  0.05), armGrain, 1, 1);
        matEdgeBot = makeSurfaceMat(v.arm.material || 'wood',
          shadeColor(v.arm.fill, -0.35), armGrain, 1, 1);
        matFront = matFace(texFront); // +Z face
        matBack  = matFace(texBack);  // -Z face
        // BoxGeometry face order: +X, -X, +Y, -Y, +Z, -Z
        armMats = [matEdge, matEdge, matEdgeTop, matEdgeBot, matFront, matBack];
      }

      // Pivot at the post centre, rotated by yaw, then push the arm
      // outward so its inner edge meets the post.
      const pivot = new THREE.Group();
      pivot.rotation.y = (a.yaw * Math.PI) / 180;

      if (useShapeArm) {
        // ── Single-piece silhouette (rect body + chevron tip) ────
        // One combined Shape: rectangle (0..bodyW) + chevron point
        // (bodyW..bodyW+tipLen). All three faces — front decal,
        // back decal, and the beveled core — share the same fill
        // colour and silhouette so the plaque reads as one piece
        // with one continuous double-line border.
        const totalW = bodyW + tipLen;
        const shape = new THREE.Shape();
        shape.moveTo(0, -armH / 2);
        shape.lineTo(bodyW, -armH / 2);
        shape.lineTo(totalW, 0);
        shape.lineTo(bodyW, armH / 2);
        shape.lineTo(0, armH / 2);
        shape.closePath();

        // Core: full-thickness extrude with a small bevel for the
        // 2–3 mm rounded edges. Painted with the same matte arm fill
        // (no texture) so the side walls and bevel rims read as the
        // same colour as the front/back faces.
        const bevel = Math.min(armDepth * 0.22, 0.0025);
        const coreDepth = Math.max(armDepth - bevel * 2, armDepth * 0.5);
        const coreGeo = new THREE.ExtrudeGeometry(shape, {
          depth: coreDepth,
          bevelEnabled: true,
          bevelThickness: bevel,
          bevelSize: bevel,
          bevelSegments: 2,
          curveSegments: 4,
        });
        // Centre the extrusion on Z so the front face sits at +armDepth/2.
        coreGeo.translate(0, 0, -coreDepth / 2 - bevel);
        const coreMat = basicMat(v.arm.fill);
        const core = new THREE.Mesh(coreGeo, coreMat);
        core.position.set(postR * 0.92, 0, 0);
        pivot.add(core);
        this._materials.push(coreMat);

        // Helper to build a ShapeGeometry decal whose UVs map the
        // shape's bounding box (0..totalW × -armH/2..armH/2) to the
        // canvas's (0..1 × 0..1) space. Default ShapeGeometry uses
        // raw vertex coords as UVs — we remap so the canvas covers
        // the silhouette exactly.
        const makeDecalGeo = (shp) => {
          const g = new THREE.ShapeGeometry(shp);
          const pos = g.attributes.position;
          const uvs = new Float32Array(pos.count * 2);
          for (let k = 0; k < pos.count; k++) {
            const px = pos.getX(k);
            const py = pos.getY(k);
            uvs[k * 2]     = px / totalW;
            uvs[k * 2 + 1] = (py + armH / 2) / armH;
          }
          g.setAttribute('uv', new THREE.BufferAttribute(uvs, 2));
          return g;
        };

        // Front decal — chevron points toward +X (outer end).
        const frontGeo = makeDecalGeo(shape);
        const frontDecalMat = new THREE.MeshBasicMaterial({
          map: texFront, transparent: true, alphaTest: 0.05,
        });
        const frontDecal = new THREE.Mesh(frontGeo, frontDecalMat);
        // 0.3 mm forward of the core's front face to avoid z-fighting.
        frontDecal.position.set(postR * 0.92, 0, armDepth / 2 + 0.0003);
        pivot.add(frontDecal);

        // Back decal — same shape, but rotated 180° around Y so its
        // visible normal faces -Z. After rotation the chevron tip
        // (originally at local +X) ends up at world -X relative to
        // the rotated mesh's local origin, so we shift the mesh by
        // +totalW in the (already-rotated) X to keep the chevron
        // anchored to the outer end of the arm in world space.
        // The back canvas is painted with `arrowOnLeft = true` so
        // the silhouette + border line up after the 180° rotation.
        const backGeo = makeDecalGeo(shape);
        const backDecalMat = new THREE.MeshBasicMaterial({
          map: texBack, transparent: true, alphaTest: 0.05,
        });
        const backDecal = new THREE.Mesh(backGeo, backDecalMat);
        backDecal.rotation.y = Math.PI;
        backDecal.position.set(postR * 0.92 + totalW, 0,
          -armDepth / 2 - 0.0003);
        pivot.add(backDecal);

        this._materials.push(frontDecalMat, backDecalMat);
      } else {
        // ── Default arm: BoxGeometry body (+ optional separate
        //    chevron tip mesh for legacy chevron variants).
        const armMesh = new THREE.Mesh(armGeo, armMats);
        armMesh.position.set(bodyW / 2 + postR * 0.92, 0, 0);
        pivot.add(armMesh);

        if (useChevron) {
          const tipShape = new THREE.Shape();
          tipShape.moveTo(0, -armH / 2);
          tipShape.lineTo(0,  armH / 2);
          tipShape.lineTo(tipLen, 0);
          tipShape.lineTo(0, -armH / 2);
          const tipBevel = Math.min(armDepth * 0.18, 0.003);
          const tipGeo = new THREE.ExtrudeGeometry(tipShape, {
            depth: Math.max(armDepth - tipBevel * 2, armDepth * 0.6),
            bevelEnabled: true,
            bevelThickness: tipBevel,
            bevelSize: tipBevel,
            bevelSegments: 2,
            curveSegments: 4,
          });
          tipGeo.translate(0, 0, -armDepth / 2);
          // Tip uses a plain matte fill with the EXACT body fill
          // colour. The body's painted canvas is now also flat-fill
          // (edge AO / wash suppressed for ACP — see paintArmCanvas)
          // so the box body face and the tip read as one continuous
          // piece with no visible seam. Wood/stone variants still
          // use the shaded tip colour below.
          const tipMat = (v.arm.material === 'acp')
            ? basicMat(v.arm.fill)
            : basicMat(shadeColor(v.arm.fill, -0.04));
          const tipMesh = new THREE.Mesh(tipGeo, tipMat);
          tipMesh.position.set(bodyW + postR * 0.92, 0, 0);
          pivot.add(tipMesh);
          this._materials.push(tipMat);
        }
      }

      // ── Mounting clamp / band ───────────────────────────────────
      // `post.clamp === 'bracket'` swaps the simple cylindrical band
      // for a more detailed fitting:
      //   • a wider top + bottom collar ring (so the bracket has
      //     visible thickness rather than a single slim hoop),
      //   • a thin recessed AO band sandwiched between them,
      //   • four hex-style brass bolts (front-top, front-bot,
      //     back-top, back-bot).
      // The arm's inner edge is slightly recessed under the clamp
      // (painted into the canvas as a left-edge AO gradient).
      const bandColor = v.post.bandColor || v.post.cap.color;
      const collarSideMat = basicMat(bandColor);
      const collarTopMat  = basicMat(shadeColor(bandColor,  0.22));
      const collarBotMat  = basicMat(shadeColor(bandColor, -0.22));
      const isBracket = v.post.clamp === 'bracket';

      if (isBracket) {
        // Two brass collar rings — top rim + bottom rim — with a
        // thin darker recess between them. Reads as a real machined
        // clamp instead of a single solid hoop.
        const ringH    = armH * 0.18;
        const ringR    = postR * 1.22;
        const ringGap  = armH * 0.42;
        const ringGeo  = new THREE.CylinderGeometry(ringR, ringR, ringH, 24);

        const topRing = new THREE.Mesh(ringGeo,
          [collarSideMat, collarTopMat, collarBotMat]);
        topRing.position.set(0, ringGap / 2, 0);
        pivot.add(topRing);

        const botRing = new THREE.Mesh(ringGeo,
          [collarSideMat, collarTopMat, collarBotMat]);
        botRing.position.set(0, -ringGap / 2, 0);
        pivot.add(botRing);

        // Note: no extra cylinder is drawn between the two rings —
        // the actual pole passes through here, so its real wood
        // texture stays visible. (An earlier version painted a
        // solid-colour cylinder in the gap; that hid the wood grain
        // and read as a different material.)

        // Four bolt heads — front-top, front-bot, back-top, back-bot.
        // Hex look comes from a 6-sided cylinder. A slim flat disc
        // sits on top to read as the bolt's flange.
        const boltR    = postR * 0.22;
        const boltLen  = armDepth * 1.6;
        const boltGeo  = new THREE.CylinderGeometry(boltR, boltR, boltLen, 6);
        const flangeR  = boltR * 1.18;
        const flangeGeo = new THREE.CylinderGeometry(flangeR, flangeR, boltLen * 0.18, 12);
        const boltMatSide = basicMat(bandColor);
        const boltMatLit  = basicMat(shadeColor(bandColor,  0.32));
        const boltMatDark = basicMat(shadeColor(bandColor, -0.30));
        const boltX = postR * 1.32;
        const boltY = ringGap / 2;
        const positions = [
          [boltX,  boltY,  armDepth * 0.55],
          [boltX, -boltY,  armDepth * 0.55],
          [boltX,  boltY, -armDepth * 0.55],
          [boltX, -boltY, -armDepth * 0.55],
        ];
        for (const [bx, by, bz] of positions) {
          const bolt = new THREE.Mesh(boltGeo,
            [boltMatSide, boltMatLit, boltMatDark]);
          // Cylinder default axis is Y; rotate so its axis is Z.
          bolt.rotation.x = Math.PI / 2;
          bolt.position.set(bx, by, bz);
          pivot.add(bolt);

          // Flange/washer slightly inboard from the head.
          const flange = new THREE.Mesh(flangeGeo,
            [boltMatSide, boltMatLit, boltMatDark]);
          flange.rotation.x = Math.PI / 2;
          flange.position.set(bx, by, bz - Math.sign(bz) * boltLen * 0.45);
          pivot.add(flange);
        }
        this._materials.push(boltMatSide, boltMatLit, boltMatDark);
      } else {
        // Legacy single-cylinder collar (variants A–E).
        const collarH = armH * 1.25;
        const collarR = postR * 1.35;
        const collarGeo = new THREE.CylinderGeometry(collarR, collarR, collarH, 16);
        const collar = new THREE.Mesh(collarGeo,
          [collarSideMat, collarTopMat, collarBotMat]);
        pivot.add(collar);
      }

      const yCentre = postTopY - topPad - i * spacing;
      pivot.position.set(0, yCentre, 0);
      // Tag the pivot so _update can find it for fast in-place yaw
      // updates (slider drag) without rebuilding the whole sign.
      pivot.userData.isSignArmPivot = true;
      pivot.userData.armIndex = i;
      group.add(pivot);

      if (!isSinglePiece) {
        this._materials.push(matFront, matBack, matEdge, matEdgeTop, matEdgeBot);
      }
      this._materials.push(collarSideMat, collarTopMat, collarBotMat);
      this._textures.push(texFront, texBack);
    });

    group.position.set(worldX, worldY, worldZ);
    group.rotation.y = (rotationYDeg * Math.PI) / 180;

    this.context.scene.add(group);
    this._group = group;
    _meshRegistry.set(hintId, group);
  } catch (err) {
    console.error('🪧 NavDirectionSignComponent.onInit failed:', err?.message, err);
  }
};

NavDirectionSignComponent.prototype.onDestroy = function () {
  const id = this.inputs.hintId;
  try {
    if (this._group && this.context?.scene) {
      this.context.scene.remove(this._group);
    }
    this._group?.traverse?.((node) => {
      if (node.geometry) node.geometry.dispose?.();
      if (node.material) {
        if (Array.isArray(node.material)) node.material.forEach((m) => m.dispose?.());
        else node.material.dispose?.();
      }
    });
    this._textures.forEach((t) => t?.dispose?.());
  } catch (_) { /* noop */ }
  _meshRegistry.delete(id);
};

// ─── Service ───────────────────────────────────────────────────────────
class NavDirectionSignService {
  constructor() {
    this._sdk = null;
    this._registered = false;
    this._language = 'mr';
    this._instances = new Map();
    this._unsubscribe = null;
    // Serialize _syncAll so rapid edits (add arm → change target →
    // change yaw) can't race. `_syncRunning` is true while a sync is
    // in flight; `_syncPending` records that another notification
    // came in and we must run again after the current one settles.
    this._syncRunning = false;
    this._syncPending = false;

    if (typeof window !== 'undefined') {
      window._navDirectionSignService = this;
    }
  }

  async initialize(sdk, language = 'mr') {
    this._sdk = sdk;
    this._language = language;

    if (!this._registered) {
      try {
        await sdk.Scene.register(COMPONENT_NAME, function () {
          return new NavDirectionSignComponent();
        });
        this._registered = true;
        console.log('🪧 NavDirectionSignService: component registered');
      } catch (err) {
        console.warn('🪧 NavDirectionSignService: failed to register component:', err?.message);
        return;
      }
    }

    await this._syncAll();

    this._unsubscribe = navHintsService.subscribe(() => {
      this._scheduleSync();
    });
  }

  /**
   * Coalescing serializer: at most one `_syncAll` runs at a time.
   * If a notification arrives mid-flight, we set `_syncPending` and
   * the in-flight run will trigger one more sync after it finishes —
   * this guarantees the model eventually reflects the latest hint
   * state without racing two `_create` calls for the same hint.
   */
  _scheduleSync() {
    if (this._syncRunning) {
      this._syncPending = true;
      return;
    }
    this._syncRunning = true;
    this._syncAll()
      .catch((err) =>
        console.warn('🪧 NavDirectionSignService: sync failed:', err?.message)
      )
      .finally(() => {
        this._syncRunning = false;
        if (this._syncPending) {
          this._syncPending = false;
          this._scheduleSync();
        }
      });
  }

  /** Build (or repaint) the per-arm canvases for this hint. */
  _ensureCanvases(hint) {
    const arms = Array.isArray(hint.signArms) ? hint.signArms : [];
    let reg = _canvasRegistry.get(hint.id);
    if (!reg) {
      reg = { armCanvases: [], armCanvasesBack: [] };
      _canvasRegistry.set(hint.id, reg);
    }
    // Resize both arrays in lock-step.
    while (reg.armCanvases.length < arms.length) {
      reg.armCanvases.push(document.createElement('canvas'));
      reg.armCanvasesBack.push(document.createElement('canvas'));
    }
    if (reg.armCanvases.length > arms.length) {
      reg.armCanvases.length = arms.length;
      reg.armCanvasesBack.length = arms.length;
    }
    // Paint each — front canvas: arrow on right, back canvas: arrow on
    // left. Text is drawn un-mirrored on both, so labels read normally
    // from either side of the post.
    const variant = hint.signVariant || 'A';
    // Pull per-hint colour / font overrides (empty string = use default).
    const armOverrides = {
      fill:      hint.signFill || '',
      text:      hint.signTextColor || '',
      border:    hint.signBorderColor || '',
      accent:    hint.signAccentColor || '',
      fontScale: Number.isFinite(hint.signFontScale) && hint.signFontScale > 0
        ? hint.signFontScale : 1.0,
    };
    // Snapshot of all hints so we can resolve targetId → POI labels.
    // Lookup by id is O(n) but n is tiny (a few dozen max).
    const allHints = navHintsService.getAll();
    // Two-pass: (1) resolve every arm's label and compute the largest
    // font size that still fits inside its outer border; (2) paint
    // every arm using the *minimum* of those sizes so all arms in a
    // single signage share a consistent, uniform font.
    const family = '900 Xpx "Noto Sans Devanagari", "Noto Serif Devanagari", "Noto Serif", "Inter", Georgia, serif';
    const layout = getArmTextLayout(variant);
    const armLabels = arms.map((a) => {
      let labelSource = a.labels;
      if (a.targetId && a.customLabels !== true) {
        const poi = allHints.find((h) => h.id === a.targetId && h.type === 'poi');
        if (poi?.labels) labelSource = poi.labels;
      }
      return (labelSource && (labelSource[this._language] || labelSource.en
              || labelSource.mr || labelSource.hi)) || '';
    });
    let minFontSize = Infinity;
    // Per-arm resolved font size: explicit `fontSizePx` override
    // wins; otherwise auto-fit against the label width.
    const armFontSizes = arms.map((a, i) => {
      const lbl = armLabels[i];
      if (Number.isFinite(a?.fontSizePx) && a.fontSizePx > 0) {
        return Math.round(a.fontSizePx);
      }
      return fitArmFontSize(null, lbl, family, layout.H,
        layout.textRegionW, armOverrides.fontScale);
    });
    for (let i = 0; i < arms.length; i++) {
      // Only un-overridden arms participate in the shared min so a
      // single explicit override doesn't shrink every other arm.
      if (Number.isFinite(arms[i]?.fontSizePx) && arms[i].fontSizePx > 0) continue;
      if (armFontSizes[i] < minFontSize) minFontSize = armFontSizes[i];
    }
    if (!Number.isFinite(minFontSize)) {
      minFontSize = Math.round(layout.H * 0.36 * armOverrides.fontScale);
    }
    // Final per-arm size: explicit override stays; un-overridden arms
    // use the shared min for visual uniformity.
    const finalArmSizes = arms.map((a, i) =>
      (Number.isFinite(a?.fontSizePx) && a.fontSizePx > 0) ? armFontSizes[i] : minFontSize
    );
    // Detect whether any arm's label actually renders on 2 lines
    // (using each arm's own resolved size and lineMode override).
    // When at least one arm wraps, all arms in this signage are
    // rendered slightly TALLER (3D mesh stretched vertically) so
    // both lines sit comfortably inside the engraved double border.
    let anyWrapped = false;
    for (let i = 0; i < arms.length; i++) {
      const lines = wrapArmLabel(null, armLabels[i], family,
        finalArmSizes[i], layout.textRegionW, arms[i]?.lineMode);
      if (lines.length > 1) { anyWrapped = true; break; }
    }
    reg.armHeightMul = anyWrapped ? 1.25 : 1.0;
    arms.forEach((a, i) => {
      const lbl = armLabels[i];
      const paintOverrides = {
        ...armOverrides,
        fontSizePx: finalArmSizes[i],
        lineMode:   a?.lineMode,
      };
      paintArmCanvas(reg.armCanvases[i],     lbl, variant, /* arrowOnLeft */ false, paintOverrides);
      paintArmCanvas(reg.armCanvasesBack[i], lbl, variant, /* arrowOnLeft */ true,  paintOverrides);
    });
  }

  _armSpec(hint) {
    const arms = Array.isArray(hint.signArms) ? hint.signArms : [];
    return arms.map((a) => ({
      yaw: typeof a.yaw === 'number' ? a.yaw : 0,
    }));
  }

  _snapshot(h) {
    return JSON.stringify({
      pos: h.position,
      rot: h.rotationY ?? 0,
      scale: h.scale ?? 1.0,
      variant: h.signVariant || 'A',
      arms: (h.signArms || []).map((a) => ({
        yaw: a.yaw,
        labels: a.labels,
        targetId: a.targetId || '',
        customLabels: a.customLabels === true,
        lineMode:   a.lineMode || 'auto',
        fontSizePx: Number.isFinite(a.fontSizePx) ? a.fontSizePx : 0,
      })),
      // Override fields: any change should rebuild meshes / repaint canvases.
      fill:      h.signFill || '',
      text:      h.signTextColor || '',
      border:    h.signBorderColor || '',
      accent:    h.signAccentColor || '',
      post:      h.signPostColor || '',
      brass:     h.signBrassColor || '',
      fontScale: h.signFontScale ?? 1.0,
    });
  }

  async _syncAll() {
    if (!this._sdk || !this._registered) return;
    const all = navHintsService.getAll().filter((h) => h.type === 'directionSign');
    const wanted = new Set(all.map((h) => h.id));

    for (const id of Array.from(this._instances.keys())) {
      if (!wanted.has(id)) this._remove(id);
    }

    for (const h of all) {
      const existing = this._instances.get(h.id);
      if (!existing) {
        await this._create(h).catch((err) =>
          console.warn(`🪧 NavDirectionSign "${h.id}" create failed:`, err?.message)
        );
      } else {
        await this._update(h).catch(() => {});
      }
    }
  }

  async _create(hint) {
    if (!this._sdk) return;
    this._ensureCanvases(hint);

    const [sceneObject] = await this._sdk.Scene.createObjects(1);
    const node = sceneObject.addNode();
    node.addComponent(COMPONENT_NAME, {
      hintId: hint.id,
      worldX: hint.position?.x ?? 0,
      worldY: hint.position?.y ?? 0,
      worldZ: hint.position?.z ?? 0,
      rotationYDeg: hint.rotationY ?? 0,
      scale: hint.scale ?? 1.0,
      variantKey: hint.signVariant || 'A',
      armSpec: this._armSpec(hint),
      armFill:   hint.signFill || '',
      armBorder: hint.signBorderColor || '',
      postColor: hint.signPostColor || '',
      capColor:  hint.signBrassColor || '',
      bandColor: hint.signBrassColor || '',
    });
    sceneObject.start();
    this._instances.set(hint.id, { sceneObject, snapshot: this._snapshot(hint) });
  }

  async _update(hint) {
    const inst = this._instances.get(hint.id);
    if (!inst) return;

    const snap = this._snapshot(hint);
    if (snap === inst.snapshot) return;

    let prev = {};
    try { prev = JSON.parse(inst.snapshot); } catch (_) { /* noop */ }

    const armCountChanged =
      (prev.arms?.length ?? 0) !== (hint.signArms?.length ?? 0);
    const prevYaws = (prev.arms || []).map((a) => a.yaw);
    const nextYaws = (hint.signArms || []).map((a) => a.yaw);
    const yawsChanged = JSON.stringify(prevYaws) !== JSON.stringify(nextYaws);
    const structuralChange =
      prev.variant !== (hint.signVariant || 'A') ||
      prev.scale !== (hint.scale ?? 1.0) ||
      armCountChanged ||
      // Colour overrides are baked into materials at onInit, so any change
      // requires a rebuild. Font/text overrides only repaint canvases — those
      // refresh via `needsUpdate` below.
      prev.post !== (hint.signPostColor || '') ||
      prev.brass !== (hint.signBrassColor || '') ||
      prev.fill !== (hint.signFill || '') ||
      prev.border !== (hint.signBorderColor || '');

    // Repaint canvases (label/language/variant changes).
    this._ensureCanvases(hint);
    const group = _meshRegistry.get(hint.id);
    if (group) {
      // Arm meshes use a 6-material array (one material per BoxGeometry
      // face); post / cap / collar use a single material. We must check
      // both shapes — `node.material.map` would otherwise resolve to
      // `Array.prototype.map` for arm meshes and silently skip the
      // texture refresh, leaving stale labels on the arms.
      group.traverse((node) => {
        const mat = node.material;
        if (!mat) return;
        const mats = Array.isArray(mat) ? mat : [mat];
        for (const m of mats) {
          if (m && m.map) m.map.needsUpdate = true;
        }
      });
      // Cheap transform updates.
      const p = hint.position || {};
      group.position.set(p.x ?? 0, p.y ?? 0, p.z ?? 0);
      group.rotation.y = ((hint.rotationY ?? 0) * Math.PI) / 180;

      // Live, in-place arm yaw updates — avoids full mesh rebuild
      // when the author drags the per-arm yaw slider, which fired
      // _create/_remove on every input event and made the slider
      // feel "stuck". Each pivot was tagged with its armIndex when
      // built; we just reapply the current yaw.
      if (yawsChanged && !structuralChange) {
        group.children.forEach((child) => {
          if (!child?.userData?.isSignArmPivot) return;
          const idx = child.userData.armIndex;
          const yaw = nextYaws[idx];
          if (Number.isFinite(yaw)) {
            child.rotation.y = (yaw * Math.PI) / 180;
          }
        });
      }
    }

    if (structuralChange) {
      this._remove(hint.id);
      await this._create(hint).catch(() => {});
      return;
    }

    inst.snapshot = snap;
  }

  _remove(id) {
    const inst = this._instances.get(id);
    if (inst) {
      try { inst.sceneObject.stop(); } catch (_) { /* noop */ }
      this._instances.delete(id);
    }
    _meshRegistry.delete(id);
    _canvasRegistry.delete(id);
  }

  setLanguage(language) {
    if (this._language === language) return;
    this._language = language;
    const all = navHintsService.getAll().filter((h) => h.type === 'directionSign');
    for (const h of all) {
      this._ensureCanvases(h);
      const group = _meshRegistry.get(h.id);
      group?.traverse?.((node) => {
        const mat = node.material;
        if (!mat) return;
        // Arms use a 6-material array (one per BoxGeometry face); the
        // post / cap / collar use a single material. Flag every map
        // we find so the front + back canvases both refresh.
        const mats = Array.isArray(mat) ? mat : [mat];
        for (const m of mats) {
          if (m && m.map) m.map.needsUpdate = true;
        }
      });
    }
  }

  destroy() {
    if (this._unsubscribe) {
      try { this._unsubscribe(); } catch (_) { /* noop */ }
      this._unsubscribe = null;
    }
    for (const id of Array.from(this._instances.keys())) {
      this._remove(id);
    }
  }

  /**
   * Per-frame proximity gate. Hides each direction sign whose floor
   * distance from `pose.position` exceeds the global `maxDistance`,
   * with optional linear fade between `fadeStart` and `maxDistance`.
   *
   * Called from NavigationHints' pose-update loop (which already runs
   * on every Matterport pose change). When the editor is open, this
   * is bypassed so authors can always see signs they're working on.
   *
   * @param {{position:{x:number,y:number,z:number}}} pose
   */
  applyProximityVisibility(pose) {
    if (!pose || !pose.position) return;
    const editorActive = navHintsService.isEditorActive?.() === true;
    const gs = navHintsService.getGlobalStyle('directionSign');
    const maxD = (gs.maxDistance && gs.maxDistance > 0) ? gs.maxDistance : 0;
    const fadeS = (gs.fadeStart && gs.fadeStart > 0) ? gs.fadeStart : 0;
    const hideW = (gs.hideWithin && gs.hideWithin > 0) ? gs.hideWithin : 0;
    if (!this._instances.size) return;

    const all = navHintsService.getAll();
    const byId = new Map();
    for (const h of all) {
      if (h && h.type === 'directionSign') byId.set(h.id, h);
    }

    for (const [id] of this._instances) {
      const group = _meshRegistry.get(id);
      if (!group) continue;
      const h = byId.get(id);
      if (!h) continue;

      // Editor mode → always show, ignore the gate.
      if (editorActive || maxD <= 0) {
        if (group.visible !== true) group.visible = true;
        // Reset any previously dimmed materials.
        if (group.userData?._dimAlpha != null) {
          this._setGroupAlpha(group, 1);
          group.userData._dimAlpha = null;
        }
        continue;
      }

      const dx = (h.position?.x ?? 0) - pose.position.x;
      const dz = (h.position?.z ?? 0) - pose.position.z;
      const dist = Math.sqrt(dx * dx + dz * dz);

      // Hard cutoffs:
      //   • dist > maxDistance → sign is too far, hide.
      //   • dist < hideWithin  → visitor is on top of / right next to
      //                          the post. Hide so the brass cap /
      //                          band / clamp meshes don't clip the
      //                          near plane and paint thin yellow
      //                          streaks across the screen.
      const wantVisible = dist <= maxD && (hideW <= 0 || dist >= hideW);
      if (group.visible !== wantVisible) group.visible = wantVisible;
      if (!wantVisible) continue;

      // Optional linear fade between fadeStart and maxD.
      let alpha = 1;
      if (fadeS > 0 && fadeS < maxD && dist > fadeS) {
        alpha = Math.max(0, 1 - (dist - fadeS) / (maxD - fadeS));
      }
      const prev = group.userData?._dimAlpha;
      // Avoid touching materials every frame when alpha hasn't moved.
      if (prev == null || Math.abs(prev - alpha) > 0.01) {
        this._setGroupAlpha(group, alpha);
        if (!group.userData) group.userData = {};
        group.userData._dimAlpha = alpha;
      }
    }
  }

  /**
   * Apply an opacity to every material in a sign group. Sets
   * material.transparent on first dim so blending kicks in.
   */
  _setGroupAlpha(group, alpha) {
    group.traverse((node) => {
      const mat = node.material;
      if (!mat) return;
      const mats = Array.isArray(mat) ? mat : [mat];
      for (const m of mats) {
        if (!m) continue;
        if (alpha < 1 && !m.transparent) m.transparent = true;
        m.opacity = alpha;
        m.needsUpdate = true;
      }
    });
  }

  /**
   * Compute the auto-fit font size (in px) that the renderer would use
   * for a given arm if its `fontSizePx` override were not set. The
   * editor uses this to show the actual displayed size as the
   * placeholder value rather than 0.
   *
   * @param {object} hint        Direction-sign hint
   * @param {number} armIndex    Index into hint.signArms
   * @param {string} [language]  Language code (defaults to service's current)
   * @returns {number}           Pixel font size; falls back to the
   *                             variant default when the label is empty.
   */
  getAutoArmFontSize(hint, armIndex, language) {
    const arms = Array.isArray(hint?.signArms) ? hint.signArms : [];
    const arm = arms[armIndex];
    if (!arm) return 0;
    const variant = hint.signVariant || 'A';
    const layout = getArmTextLayout(variant);
    const fontScale = Number.isFinite(hint.signFontScale) && hint.signFontScale > 0
      ? hint.signFontScale : 1.0;
    // Resolve the displayed label (mirrors _ensureCanvases logic).
    let labelSource = arm.labels;
    if (arm.targetId && arm.customLabels !== true) {
      const allHints = navHintsService.getAll();
      const poi = allHints.find((h) => h.id === arm.targetId && h.type === 'poi');
      if (poi?.labels) labelSource = poi.labels;
    }
    const lang = language || this._language || 'mr';
    const lbl = (labelSource && (labelSource[lang] || labelSource.en
            || labelSource.mr || labelSource.hi)) || '';
    const family = '900 Xpx "Noto Sans Devanagari", "Noto Serif Devanagari", "Noto Serif", "Inter", Georgia, serif';
    return fitArmFontSize(null, lbl, family, layout.H, layout.textRegionW, fontScale);
  }
}

const navDirectionSignService = new NavDirectionSignService();
export default navDirectionSignService;
